import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/system-users/SystemUsersAction";
import * as uiHelpers from "../SystemUsersUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useSystemUsersUIContext } from "../SystemUsersUIContext";

export function SystemUsersTable({ deleteSystemUsers }) {
  // SystemUsers UI Context
  const systemUsersUIContext = useSystemUsersUIContext();
  const systemUsersUIProps = useMemo(() => {
    return {
      ids: systemUsersUIContext.ids,
      setIds: systemUsersUIContext.setIds,
      queryParams: systemUsersUIContext.queryParams,
      setQueryParams: systemUsersUIContext.setQueryParams,
      openEditSystemUserPage: systemUsersUIContext.openEditSystemUserPage
    };
  }, [systemUsersUIContext]);

  // Getting curret state of SystemUsers list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.systemUsers }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // SystemUsers Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    // clear selections list
    systemUsersUIProps.setIds([]);
    dispatch(actions.getSystemUsers(systemUsersUIProps.queryParams));
    return () => dispatch(actions.cleanSystemUsersState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemUsersUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "surname",
      text: "Soyadı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "username",
      text: "Kullanıcı Adı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "isSuperAdmin",
      text: "Super Admin",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.ConfirmationColumnFormatter,
    },
    {
      dataField: "isActive",
      text: "Aktif mi?",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditSystemUserPage: systemUsersUIProps.openEditSystemUserPage,
      deleteSystemUsers
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: systemUsersUIProps.queryParams.pageSize,
    page: systemUsersUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ? entities : []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                systemUsersUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: systemUsersUIProps.ids,
                setIds: systemUsersUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}